import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { GapStylingConfig, defaultConfig } from './gap-styling';
import { GapStylingService } from './GapStylingService';
import './gap-styling-animations.css';

interface RootComponentProps {
  children: React.ReactNode;
  config?: GapStylingConfig;
  autoLoad?: boolean;
  className?: string;
  onConfigLoaded?: (config: GapStylingConfig) => void;
}

export const RootComponent: React.FC<RootComponentProps> = ({
  children,
  config: providedConfig,
  autoLoad = true,
  className = '',
  onConfigLoaded
}) => {
  const [gapConfig, setGapConfig] = useState<GapStylingConfig>(providedConfig || defaultConfig);
  const [loading, setLoading] = useState(autoLoad && !providedConfig);

  // Load configuration from Firebase if autoLoad is enabled and no config is provided
  useEffect(() => {
    if (autoLoad && !providedConfig) {
      loadConfiguration();
    } else if (providedConfig) {
      setGapConfig(providedConfig);
    }
  }, [autoLoad, providedConfig]);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const loadedConfig = await GapStylingService.loadConfig();
      
      if (loadedConfig) {
        const mergedConfig = { ...defaultConfig, ...loadedConfig };
        setGapConfig(mergedConfig);
        onConfigLoaded?.(mergedConfig);
      } else {
        setGapConfig(defaultConfig);
        onConfigLoaded?.(defaultConfig);
      }
    } catch (error) {
      console.error('Failed to load gap styling configuration:', error);
      setGapConfig(defaultConfig);
      onConfigLoaded?.(defaultConfig);
    } finally {
      setLoading(false);
    }
  };

  // Generate CSS for gap styling
  const generateGapCSS = (config: GapStylingConfig): string => {
    if (!config.enabled) return '';

    let background = '';
    switch (config.backgroundType) {
      case 'color':
        background = config.backgroundColor;
        break;
      case 'gradient': {
        const { gradientType, gradientDirection, gradientColors } = config;
        const colorStops = gradientColors
          .sort((a, b) => a.position - b.position)
          .map(color => `${color.color} ${color.position}%`)
          .join(', ');
        
        switch (gradientType) {
          case 'linear':
            background = `linear-gradient(${gradientDirection}, ${colorStops})`;
            break;
          case 'radial':
            background = `radial-gradient(circle, ${colorStops})`;
            break;
          case 'conic':
            background = `conic-gradient(from ${gradientDirection}, ${colorStops})`;
            break;
        }
        break;
      }
      case 'image':
        background = config.backgroundImage 
          ? `url(${config.backgroundImage})` 
          : config.backgroundColor;
        break;
      case 'pattern': {
        const { patternType, patternColor, patternSecondaryColor, patternSize } = config;
        switch (patternType) {
          case 'dots':
            background = `radial-gradient(circle at ${patternSize}px ${patternSize}px, ${patternColor} 2px, ${patternSecondaryColor} 2px)`;
            break;
          case 'stripes':
            background = `repeating-linear-gradient(45deg, ${patternColor}, ${patternColor} ${patternSize}px, ${patternSecondaryColor} ${patternSize}px, ${patternSecondaryColor} ${patternSize * 2}px)`;
            break;
          case 'waves':
            background = `radial-gradient(ellipse at top, ${patternColor}, transparent), radial-gradient(ellipse at bottom, ${patternSecondaryColor}, transparent)`;
            break;
          case 'checkerboard':
            background = `linear-gradient(45deg, ${patternColor} 25%, transparent 25%, transparent 75%, ${patternColor} 75%), linear-gradient(45deg, ${patternColor} 25%, transparent 25%, transparent 75%, ${patternColor} 75%)`;
            break;
          case 'zigzag':
            background = `linear-gradient(135deg, ${patternColor} 25%, transparent 25%) -${patternSize}px 0, linear-gradient(225deg, ${patternColor} 25%, transparent 25%) -${patternSize}px 0, linear-gradient(315deg, ${patternColor} 25%, transparent 25%), linear-gradient(45deg, ${patternColor} 25%, transparent 25%)`;
            break;
        }
        break;
      }
    }

    const boxShadowValue = config.boxShadow.enabled 
      ? `${config.boxShadow.inset ? 'inset ' : ''}${config.boxShadow.x}px ${config.boxShadow.y}px ${config.boxShadow.blur}px ${config.boxShadow.spread}px ${config.boxShadow.color}`
      : 'none';

    const animationValue = config.animation.enabled && config.animation.type !== 'none'
      ? `gap-animation-${config.animation.type} ${config.animation.duration}s ${config.animation.infinite ? 'infinite' : ''}`
      : 'none';

    return `
      /* Root Component Gap Styling - Generated CSS */
      .gap-styled-root .MUIDataTable-responsiveBase tbody tr:not(:last-child),
      .gap-styled-root .MUIDataTable-tableBody tr:not(:last-child),
      .gap-styled-root table tbody tr:not(:last-child) {
        position: relative;
        margin-bottom: ${config.height + config.margin.top + config.margin.bottom}px;
      }
      
      .gap-styled-root .MUIDataTable-responsiveBase tbody tr:not(:last-child)::after,
      .gap-styled-root .MUIDataTable-tableBody tr:not(:last-child)::after,
      .gap-styled-root table tbody tr:not(:last-child)::after {
        content: '';
        display: block;
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        height: ${config.height}px;
        background: ${background};
        ${config.backgroundType === 'image' ? `background-repeat: ${config.imageRepeat};` : ''}
        ${config.backgroundType === 'image' ? `background-size: ${config.imageSize === 'custom' ? `${config.imageSizeWidth} ${config.imageSizeHeight}` : config.imageSize};` : ''}
        ${config.backgroundType === 'image' ? `background-position: ${config.imagePosition};` : ''}
        opacity: ${config.opacity};
        border-radius: ${config.borderRadius}px;
        ${config.borderWidth > 0 ? `border: ${config.borderWidth}px ${config.borderStyle} ${config.borderColor};` : ''}
        box-shadow: ${boxShadowValue};
        margin: ${config.margin.top}px ${config.margin.right}px ${config.margin.bottom}px ${config.margin.left}px;
        mix-blend-mode: ${config.blendMode};
        animation: ${animationValue};
        z-index: 1;
      }

      /* Custom gap elements for manual placement */
      .gap-styled-root .raffle-gap {
        height: ${config.height}px;
        background: ${background};
        ${config.backgroundType === 'image' ? `background-repeat: ${config.imageRepeat};` : ''}
        ${config.backgroundType === 'image' ? `background-size: ${config.imageSize === 'custom' ? `${config.imageSizeWidth} ${config.imageSizeHeight}` : config.imageSize};` : ''}
        ${config.backgroundType === 'image' ? `background-position: ${config.imagePosition};` : ''}
        opacity: ${config.opacity};
        border-radius: ${config.borderRadius}px;
        ${config.borderWidth > 0 ? `border: ${config.borderWidth}px ${config.borderStyle} ${config.borderColor};` : ''}
        box-shadow: ${boxShadowValue};
        margin: ${config.margin.top}px ${config.margin.right}px ${config.margin.bottom}px ${config.margin.left}px;
        mix-blend-mode: ${config.blendMode};
        animation: ${animationValue};
        position: relative;
        width: 100%;
      }

      /* Responsive adjustments */
      @media (max-width: 768px) {
        .gap-styled-root .raffle-gap {
          height: ${Math.max(config.height * 0.7, 10)}px;
          margin: ${config.margin.top * 0.7}px ${config.margin.right * 0.7}px ${config.margin.bottom * 0.7}px ${config.margin.left * 0.7}px;
        }
      }
    `;
  };

  // Generate inline styles for the root container
  const getRootStyles = (): React.CSSProperties => ({
    position: 'relative',
    width: '100%',
  });

  if (loading) {
    return (
      <Box 
        className={`gap-styled-root ${className}`}
        sx={{
          ...getRootStyles(),
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px'
        }}
      >
        Loading gap styling...
      </Box>
    );
  }

  return (
    <Box 
      className={`gap-styled-root ${className}`}
      sx={getRootStyles()}
    >
      {/* Inject dynamic CSS for gap styling */}
      {gapConfig.enabled && (
        <style>
          {generateGapCSS(gapConfig)}
        </style>
      )}
      
      {children}
    </Box>
  );
};

// Helper component to manually insert gaps
export const RaffleGap: React.FC<{ 
  config?: GapStylingConfig;
  className?: string;
  style?: React.CSSProperties;
}> = ({ 
  config, 
  className = '', 
  style = {} 
}) => {
  if (!config?.enabled) return null;

  return (
    <div 
      className={`raffle-gap ${className}`}
      style={style}
    />
  );
};

// Hook to use gap styling configuration
export const useGapStyling = () => {
  const [config, setConfig] = useState<GapStylingConfig>(defaultConfig);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const loadedConfig = await GapStylingService.loadConfig();
      
      if (loadedConfig) {
        setConfig({ ...defaultConfig, ...loadedConfig });
      } else {
        setConfig(defaultConfig);
      }
    } catch (error) {
      console.error('Failed to load gap styling configuration:', error);
      setConfig(defaultConfig);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async (newConfig: GapStylingConfig) => {
    try {
      await GapStylingService.saveConfig(newConfig);
      setConfig(newConfig);
    } catch (error) {
      console.error('Failed to save gap styling configuration:', error);
      throw error;
    }
  };

  const resetConfig = async () => {
    try {
      await GapStylingService.resetConfig();
      setConfig(defaultConfig);
    } catch (error) {
      console.error('Failed to reset gap styling configuration:', error);
      throw error;
    }
  };

  return {
    config,
    loading,
    loadConfig,
    saveConfig,
    resetConfig
  };
};

export default RootComponent;